import type { Moment } from 'moment';
import { machineStatusColorName } from '@components/wallboard/components/functions';
import type { ScaleFunction } from '@components/operatorPanel/interfaces';
import type { ColorFunction } from '@components/common/CircleBar';
import type { MachineEvent, BatchTimelineTimeState } from '@components/wallboard/types';
import type { GroupedMachineEvent } from '@api/domain/types';
import type { MachineEventSearchItem } from '@interfaces/machines';

export const toMachineEvent = (dto: GroupedMachineEvent): MachineEvent => ({
  startedOn: dto.startedOn,
  endedOn: dto.endedOn,
  status: dto.status,
  subStatus: dto.subStatus,
  productionBatchId: dto.productionBatchId,
  quantityDelta: dto.quantityDelta,
  ncFileMain: dto.ncFileMain,
  ncFileStatusMain: dto.ncFileStatus,
  executionMode: dto.executionMode,
});

export const toMachineEventSearchItem = (dto: GroupedMachineEvent): MachineEventSearchItem => ({
  startedOn: dto.startedOn,
  endedOn: dto.endedOn,
  status: dto.status,
  subStatus: dto.subStatus,
  productionBatchId: dto.productionBatchId,
  quantityDelta: dto.quantityDelta,
  ncFileStatus: dto.ncFileStatus,
  ncFileName: dto.ncFileMain,
  executionMode: dto.executionMode,
  sequenceNumber: dto.sequenceNumberStartedOn,
});

export const fetchAllMachineEventData = async (
  productionBatchId: number,
  getMachineEventTimelineByBatchId: (filters: Record<string, unknown>, signal: AbortSignal) => Promise<any>,
  signal: AbortSignal,
  pageSize = 1000
): Promise<MachineEvent[]> => {
  let allMachineEvents: MachineEvent[] = [];
  let currentPage = 1;
  let hasNext = true;

  while (hasNext && !signal.aborted) {
    const filters = {
      productionBatchId,
      useAsyncFlowForRealtimeQuery: true,
      pagination: {
        pageNumber: currentPage,
        pageSize,
        includeCount: true,
      },
    };

    const data = await getMachineEventTimelineByBatchId(filters, signal);

    if (data?.machineEvents) {
      allMachineEvents.push(...data.machineEvents);
    }

    try {
      const paginationInfo = data?.headers?.paginationString ? JSON.parse(data.headers.paginationString) : null;
      hasNext = paginationInfo?.HasNext === true;
    } catch {
      hasNext = false;
    }

    currentPage++;
  }

  return allMachineEvents;
};

export const computeRadiusStyle = (
  roundOnlyTop: boolean,
  roundOnlyBottom: boolean,
  borderRadius: number,
  scaleFn: ScaleFunction
): Record<string, number> => {
  if (roundOnlyTop) {
    return {
      borderTopLeftRadius: scaleFn(borderRadius),
      borderTopRightRadius: scaleFn(borderRadius),
    };
  }
  if (roundOnlyBottom) {
    return {
      borderBottomLeftRadius: scaleFn(borderRadius),
      borderBottomRightRadius: scaleFn(borderRadius),
    };
  }
  return { borderRadius: scaleFn(borderRadius) };
};

export const computeContainerBorderRadiusCss = (
  roundOnlyTop: boolean,
  roundOnlyBottom: boolean,
  borderRadius: number
): string => {
  if (roundOnlyTop) {
    return `border-top-left-radius: ${borderRadius}px; border-top-right-radius: ${borderRadius}px;`;
  }
  if (roundOnlyBottom) {
    return `border-bottom-left-radius: ${borderRadius}px; border-bottom-right-radius: ${borderRadius}px;`;
  }
  return `border-radius: ${borderRadius}px;`;
};

export const generateBatchTimelineHtml = (params: {
  machineEventData: MachineEvent[];
  height: number;
  containerBorderRadiusCss: string;
  containerBg: string;
  durationInMs: number;
  startTime: Moment;
  localTimeFn: (t?: string | Date | number | Moment) => Moment;
  withGaps: boolean;
  roundOnlyTop: boolean;
  roundOnlyBottom: boolean;
  borderRadius: number;
  colorFn: ColorFunction;
}): string => {
  const {
    machineEventData,
    height,
    containerBorderRadiusCss,
    containerBg,
    durationInMs,
    startTime,
    localTimeFn,
    withGaps,
    roundOnlyTop,
    roundOnlyBottom,
    borderRadius,
    colorFn,
  } = params;

  const baseStyle = `
    body, html { margin: 0; padding: 0; height: ${height}px; width: 100%; overflow: hidden; }
    .timeline-container {
      position: relative; width: 100%; height: ${height}px;
      ${containerBorderRadiusCss}
      background-color: ${containerBg};
      overflow: hidden;
    }
  `;

  if (machineEventData.length === 0) {
    return `<html><head><style>${baseStyle}</style></head><body><div class="timeline-container"></div></body></html>`;
  }

  let cumulativeWidth = 0;
  const htmlEvents = machineEventData
    .map((event, index) => {
      const isLastEvent = index === machineEventData.length - 1;
      const started = localTimeFn(event.startedOn);
      const ended = isLastEvent ? localTimeFn() : localTimeFn(event.endedOn);
      const eventDuration = ended.diff(started);
      const offsetFromStart = started.diff(startTime);

      const widthPercent = (eventDuration / durationInMs) * 100;
      let leftPercent: number;

      if (withGaps) {
        leftPercent = (offsetFromStart / durationInMs) * 100;
      } else {
        leftPercent = cumulativeWidth;
        cumulativeWidth += widthPercent;
      }

      const color = machineStatusColorName?.[event.status] || containerBg;

      return `
        <div class="batch-event-block" id="${index}"
          style="position: absolute;
            left: ${leftPercent}%;
            width: ${widthPercent}%;
            height: 100%;
            background-color: ${colorFn(color)};
            border-top-left-radius: ${index === 0 && !roundOnlyBottom ? borderRadius : 0}px;
            border-bottom-left-radius: ${index === 0 && !roundOnlyTop ? borderRadius : 0}px;">
        </div>
      `;
    })
    .join('');

  return `
    <html><head><style>${baseStyle}</style></head>
    <body><div class="timeline-container">${htmlEvents}</div></body>
    </html>
  `;
};

export const computeRunningValue = (params: {
  isInProgress: boolean;
  staticStartValue: number;
  offsetInSeconds: number;
  elapsedSeconds: number;
}): number => {
  const { isInProgress, staticStartValue, offsetInSeconds, elapsedSeconds } = params;
  if (!isInProgress) {
    return staticStartValue + offsetInSeconds;
  }
  return staticStartValue + offsetInSeconds + elapsedSeconds;
};

export const computeTimelineTimeState = (value: number, targetTimeValue: number): BatchTimelineTimeState => {
  const isTargetDuration = targetTimeValue > 0;
  const hasExceededTarget = isTargetDuration && value > targetTimeValue;
  const overtimeTime = hasExceededTarget ? value - targetTimeValue : null;
  const remainingTime = !hasExceededTarget && isTargetDuration ? -(targetTimeValue - value) : null;
  const remainingOrExceededTime = hasExceededTarget ? overtimeTime : remainingTime;

  return { isTargetDuration, hasExceededTarget, overtimeTime, remainingTime, remainingOrExceededTime };
};
